import { Button } from "@/components/ui/button";
import { Loader2, FileText, Receipt, Calculator } from "lucide-react";
import type { DocumentType } from "@/utils/documentMeta";

interface PaymentActionButtonsProps {
  onSend: (docType: DocumentType) => void;
  sendingType: DocumentType | null;
  hasEmail: boolean;
  hasPhone: boolean;
  paidWithCash?: boolean;
  includePdfInvoice?: boolean;
  includeReviewLink?: boolean;
}

const PaymentActionButtons = ({
  onSend,
  sendingType,
  hasEmail,
  hasPhone,
  paidWithCash,
  includePdfInvoice,
  includeReviewLink,
}: PaymentActionButtonsProps) => {
  const busy = sendingType !== null;
  const channels =
    hasEmail && hasPhone
      ? "Email & SMS"
      : hasEmail
      ? "Email"
      : hasPhone
      ? "SMS"
      : "Email or SMS required";

  // Per-button disable rules from toggle state
  const invoiceDisabled = paidWithCash;
  const receiptDisabled = includePdfInvoice;
  const estimateDisabled = paidWithCash || includePdfInvoice || includeReviewLink;

  const renderBtn = (
    docType: DocumentType,
    label: string,
    Icon: typeof FileText,
    variant: "default" | "outline" | "secondary",
    ruleDisabled: boolean
  ) => (
    <Button
      type="button"
      onClick={() => onSend(docType)}
      disabled={busy || (!hasEmail && !hasPhone) || ruleDisabled}
      variant={variant}
      className={`w-full font-medium ${
        variant === "default" ? "bg-mint hover:bg-mint-dark" : ""
      }`}
    >
      {sendingType === docType ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Sending {label}...
        </>
      ) : (
        <>
          <Icon className="mr-2 h-4 w-4" />
          {label}
        </>
      )}
    </Button>
  );

  const warnings: string[] = [];
  if (paidWithCash) {
    warnings.push("Invoice & Estimate disabled — use the Receipt button for cash payments");
  }
  if (includePdfInvoice) {
    warnings.push("Receipt & Estimate disabled — “Include PDF Invoice” only applies to the Invoice button");
  }
  if (includeReviewLink && !paidWithCash && !includePdfInvoice) {
    warnings.push("Estimate disabled — review links only send with Invoice or Receipt");
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground text-center">
        Will be sent via: <span className="font-medium">{channels}</span>
      </p>
      {warnings.length > 0 && (
        <div className="space-y-1">
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-center text-destructive">
              {w}
            </p>
          ))}
        </div>
      )}
      {renderBtn("invoice", "Invoice", FileText, "default", invoiceDisabled)}
      {renderBtn("receipt", "Receipt", Receipt, "outline", receiptDisabled)}
      {renderBtn("estimate", "Estimate", Calculator, "outline", estimateDisabled)}
    </div>
  );
};

export default PaymentActionButtons;

